import React, { useEffect, useRef, useState } from 'react';
import {
  Space,
  Typography,
  Button,
  Input,
  Tag,
  Alert,
  Tooltip,
} from 'antd';
import {
  RobotOutlined,
  CopyOutlined,
  CheckCircleOutlined,
  SendOutlined,
  LoadingOutlined,
  SafetyCertificateOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { message } from 'antd';
import { AIReplyGenerateResponse } from '../../../services/aiReplyService';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

// ---------------------------------------------------------------------------
// Design tokens
// ---------------------------------------------------------------------------

const T = {
  brand: '#FF4500',
  brandHover: '#E03D00',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  bgSubtle: '#F6F7F8',
  border: '#E5E7EB',
  borderStrong: '#D5D7DB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
  accent: '#0079D3',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AiMessage = {
  role: 'ai';
  reply: AIReplyGenerateResponse;
  variations?: AIReplyGenerateResponse[];
  selectedVariantKey?: string;
  isAdvanced?: boolean;
};

export type UserMessage = {
  role: 'user';
  text: string;
};

export type ChatMessage = AiMessage | UserMessage;

interface ConversationViewProps {
  messages: ChatMessage[];
  refining: boolean;
  onRefine: (instruction: string) => void;
  /** Notified when the user switches between advanced variant pills so the
   *  parent can auto-save the newly selected variant. */
  onVariantSelected?: (variant: AIReplyGenerateResponse) => void;
  savedReplyTexts: Set<string>;
  /** Total number of versions persisted so far (used for the small inline
   *  "v{N}" badge above the latest reply). */
  historyCount?: number;
}

// ---------------------------------------------------------------------------
// Variant selector
// ---------------------------------------------------------------------------

const VARIANT_TABS = [
  { key: 'safe', label: 'Safe', accent: '#10B981' },
  { key: 'engaging', label: 'Engaging', accent: '#0079D3' },
  { key: 'persuasive', label: 'Persuasive', accent: '#FF4500' },
] as const;

interface AdvancedVariantBubbleProps {
  variations: AIReplyGenerateResponse[];
  onVariantSelected?: (variant: AIReplyGenerateResponse) => void;
  savedReplyTexts: Set<string>;
}

const AdvancedVariantBubble: React.FC<AdvancedVariantBubbleProps> = ({
  variations,
  onVariantSelected,
  savedReplyTexts,
}) => {
  const [selectedKey, setSelectedKey] = useState<string>(VARIANT_TABS[0].key);

  const selectedIdx = VARIANT_TABS.findIndex((t) => t.key === selectedKey);
  const selectedVariant = variations[selectedIdx] ?? variations[0];
  const tab = VARIANT_TABS[selectedIdx] ?? VARIANT_TABS[0];

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const handleVariantSwitch = (key: string) => {
    if (key === selectedKey) return;
    setSelectedKey(key);
    const nextIdx = VARIANT_TABS.findIndex((t) => t.key === key);
    const nextVariant = variations[nextIdx];
    if (nextVariant && onVariantSelected) {
      onVariantSelected(nextVariant);
    }
  };

  const isSaved = !!selectedVariant && savedReplyTexts.has(selectedVariant.final_reply);

  return (
    <div>
      {/* Variant selector pills */}
      <div
        style={{
          display: 'inline-flex',
          padding: 3,
          marginBottom: 12,
          gap: 2,
          background: T.bgSubtle,
          borderRadius: 8,
          border: `1px solid ${T.border}`,
        }}
      >
        {VARIANT_TABS.map((t) => {
          const active = selectedKey === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => handleVariantSwitch(t.key)}
              style={{
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: active ? 600 : 500,
                padding: '5px 12px',
                borderRadius: 6,
                border: 'none',
                background: active ? T.bg : 'transparent',
                color: active ? T.text : T.textSecondary,
                boxShadow: active ? '0 1px 2px rgba(15,15,16,0.08)' : 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                transition: 'all 0.12s ease',
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: t.accent,
                  display: 'inline-block',
                }}
              />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Selected variant reply */}
      {selectedVariant && (
        <div>
          {selectedVariant.warnings && selectedVariant.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message="Heads up"
              style={{ marginBottom: 10, borderRadius: 6 }}
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {selectedVariant.warnings.map((w, i) => (
                    <li key={i} style={{ fontSize: 12 }}>
                      {w}
                    </li>
                  ))}
                </ul>
              }
            />
          )}
          {selectedVariant.metadata?.guidelines_applied && (
            <div
              style={{
                marginBottom: 10,
                padding: '6px 10px',
                background: '#ECFDF5',
                border: '1px solid #A7F3D0',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <SafetyCertificateOutlined style={{ color: '#047857', fontSize: 12 }} />
              <Text style={{ fontSize: 12, color: '#065F46' }}>
                Compliant with {selectedVariant.metadata.guidelines_rule_count} r/
                {selectedVariant.metadata.subreddit} rule
                {selectedVariant.metadata.guidelines_rule_count === 1 ? '' : 's'}
              </Text>
            </div>
          )}
          <div
            style={{
              backgroundColor: T.bg,
              border: `1px solid ${T.border}`,
              borderLeft: `3px solid ${tab.accent}`,
              borderRadius: 8,
              padding: '12px 14px',
              marginBottom: 10,
            }}
          >
            <Paragraph
              style={{
                margin: 0,
                fontSize: 13.5,
                lineHeight: 1.65,
                whiteSpace: 'pre-wrap',
                color: T.text,
                fontWeight: 400,
              }}
            >
              {selectedVariant.final_reply}
            </Paragraph>
          </div>

          <Space size={8} align="center">
            <Button
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copy(selectedVariant.final_reply)}
              style={{ borderRadius: 6, borderColor: T.border, color: T.text }}
            >
              Copy
            </Button>
            {isSaved && (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontSize: 12,
                  color: '#047857',
                  fontWeight: 500,
                }}
              >
                <CheckCircleOutlined style={{ fontSize: 12 }} />
                Auto-saved
              </span>
            )}
          </Space>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Single Quick Reply AI bubble
// ---------------------------------------------------------------------------

interface QuickReplyBubbleProps {
  response: AIReplyGenerateResponse;
  isSaved: boolean;
}

const QuickReplyBubble: React.FC<QuickReplyBubbleProps> = ({ response, isSaved }) => {
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  return (
    <div>
      {response.warnings && response.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 10, borderRadius: 6 }}
          message="Heads up"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {response.warnings.map((w, i) => (
                <li key={i} style={{ fontSize: 12 }}>
                  {w}
                </li>
              ))}
            </ul>
          }
        />
      )}
      {response.metadata?.guidelines_applied && (
        <div
          style={{
            marginBottom: 10,
            padding: '6px 10px',
            background: '#ECFDF5',
            border: '1px solid #A7F3D0',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          <SafetyCertificateOutlined style={{ color: '#047857', fontSize: 12 }} />
          <Text style={{ fontSize: 12, color: '#065F46' }}>
            Compliant with {response.metadata.guidelines_rule_count} r/{response.metadata.subreddit} rule
            {response.metadata.guidelines_rule_count === 1 ? '' : 's'}
          </Text>
        </div>
      )}

      <div
        style={{
          backgroundColor: T.bg,
          border: `1px solid ${T.border}`,
          borderLeft: `3px solid ${T.brand}`,
          borderRadius: 8,
          padding: '12px 14px',
          marginBottom: 10,
        }}
      >
        <Paragraph
          style={{
            margin: 0,
            fontSize: 13.5,
            lineHeight: 1.65,
            whiteSpace: 'pre-wrap',
            color: T.text,
            fontWeight: 400,
          }}
        >
          {response.final_reply}
        </Paragraph>
      </div>

      <Space size={8} align="center">
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copy(response.final_reply)}
          style={{ borderRadius: 6, borderColor: T.border, color: T.text }}
        >
          Copy
        </Button>
        {isSaved && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 12,
              color: '#047857',
              fontWeight: 500,
            }}
          >
            <CheckCircleOutlined style={{ fontSize: 12 }} />
            Auto-saved
          </span>
        )}
      </Space>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main ConversationView
// ---------------------------------------------------------------------------

const ConversationView: React.FC<ConversationViewProps> = ({
  messages,
  refining,
  onRefine,
  onVariantSelected,
  savedReplyTexts,
  historyCount,
}) => {
  const [instruction, setInstruction] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, refining]);

  const handleSend = () => {
    const trimmed = instruction.trim();
    if (!trimmed || refining) return;
    setInstruction('');
    onRefine(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Show only the latest AI message. Earlier versions are accessible via the
  // History button in the drawer header. The last user instruction (if any)
  // is rendered as a small caption above the AI bubble for context.
  const latestAiMsg = [...messages]
    .reverse()
    .find((m): m is AiMessage => m.role === 'ai');
  const latestUserMsg = [...messages]
    .reverse()
    .find((m): m is { role: 'user'; text: string } => m.role === 'user');

  const totalVersions =
    typeof historyCount === 'number' && historyCount > 0
      ? historyCount
      : messages.filter((m) => m.role === 'ai').length;
  const hasRefinement = totalVersions > 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Messages area — only the most recent AI reply is shown. */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {/* Refinement caption — shows the instruction that produced the
              currently-displayed reply, so the user has context for it. */}
          {hasRefinement && latestUserMsg && !refining && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 10px',
                background: T.bgSubtle,
                border: `1px dashed ${T.border}`,
                borderRadius: 6,
              }}
            >
              <Text
                style={{
                  fontSize: 10,
                  color: T.textMuted,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  flexShrink: 0,
                }}
              >
                Refined
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  color: T.textSecondary,
                  fontStyle: 'italic',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                "{latestUserMsg.text}"
              </Text>
            </div>
          )}

          {latestAiMsg && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              {/* Avatar */}
              <div
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: T.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginTop: 2,
                }}
              >
                <RobotOutlined style={{ color: '#fff', fontSize: 14 }} />
              </div>

              {/* Bubble */}
              <div
                style={{
                  flex: 1,
                  backgroundColor: T.bgMuted,
                  border: `1px solid ${T.border}`,
                  borderRadius: '4px 12px 12px 12px',
                  padding: '12px 14px',
                }}
              >
                <div
                  style={{
                    marginBottom: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Text
                    style={{
                      fontSize: 11,
                      color: T.textMuted,
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.4,
                    }}
                  >
                    AI Reply
                  </Text>
                  {latestAiMsg.isAdvanced && (
                    <Tag
                      style={{
                        margin: 0,
                        fontSize: 10,
                        padding: '0 5px',
                        lineHeight: '16px',
                        background: '#FFEDE0',
                        color: T.brand,
                        border: '1px solid #FFD4BC',
                        fontWeight: 600,
                      }}
                    >
                      Advanced
                    </Tag>
                  )}
                  <Tag
                    style={{
                      margin: 0,
                      fontSize: 10,
                      padding: '0 5px',
                      lineHeight: '16px',
                      background: hasRefinement ? '#EFF6FF' : '#ECFDF5',
                      color: hasRefinement ? T.accent : '#047857',
                      border: `1px solid ${hasRefinement ? '#DBEAFE' : '#A7F3D0'}`,
                      fontWeight: 600,
                    }}
                  >
                    {hasRefinement ? `v${totalVersions}` : 'Original'}
                  </Tag>
                </div>

                {latestAiMsg.variations ? (
                  <AdvancedVariantBubble
                    variations={latestAiMsg.variations}
                    savedReplyTexts={savedReplyTexts}
                    onVariantSelected={onVariantSelected}
                  />
                ) : (
                  <QuickReplyBubble
                    response={latestAiMsg.reply}
                    isSaved={savedReplyTexts.has(latestAiMsg.reply.final_reply)}
                  />
                )}
              </div>
            </div>
          )}

          {/* Refining spinner */}
          {refining && (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div
                style={{
                  flexShrink: 0,
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: T.brand,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <LoadingOutlined style={{ color: '#fff', fontSize: 14 }} />
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: T.bgMuted,
                  border: `1px solid ${T.border}`,
                  borderRadius: '4px 12px 12px 12px',
                  padding: '12px 14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <LoadingOutlined style={{ color: T.brand, fontSize: 12 }} />
                <Text style={{ fontSize: 13, color: T.textSecondary }}>
                  Refining your reply…
                </Text>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </Space>
      </div>

      {/* Refinement input */}
      <div
        style={{
          flexShrink: 0,
          borderTop: `1px solid ${T.border}`,
          paddingTop: 12,
          marginTop: 8,
        }}
      >
        <div style={{ marginBottom: 6, display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <Text style={{ fontSize: 12, color: T.text, fontWeight: 600 }}>
            Refine this reply
          </Text>
          <Text style={{ fontSize: 11, color: T.textMuted }}>
            Shift+Enter for newline · Enter to send
          </Text>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <TextArea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='e.g. "Make it shorter", "Add a personal anecdote", "Sound less salesy"'
            autoSize={{ minRows: 2, maxRows: 5 }}
            disabled={refining}
            style={{ flex: 1, fontSize: 13, borderRadius: 8 }}
          />
          <Tooltip title="Send refinement (Enter)">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSend}
              disabled={!instruction.trim() || refining}
              loading={refining}
              style={{
                background: T.brand,
                borderColor: T.brand,
                height: 40,
                width: 40,
                flexShrink: 0,
                borderRadius: 8,
              }}
            />
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default ConversationView;
